import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  FormControl,
  FormControlLabel,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  Slide,
  TextField,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import Alert from "@material-ui/lab/Alert";

import {
  Distributor,
  parseSizing,
  Product,
  ProductSizing,
  Variant
} from "../../../models/Product";
import * as ProductApi from "../../../api/productApi";
import useStyles from "./styles";
import { CategorySelect } from "../../../components/selects/CategorySelect";
import { cartesian, parseNumberStrict } from "../../../utils";
import { Barcode } from "../../../components/Barcode";
import { DistributorSelect } from "../../../components/selects/DistributorSelect";
import { ImageEditor } from "../../../components/editors/ImageEditor";
import { OrderUnit } from "../../../models/Order";
import { useTheme } from "@material-ui/core/styles";
// @ts-ignore
import ScanditBarcodeScanner from "scandit-sdk-react";
import { ImageInformation } from "../../../models/ImageInformation";
import { PrioritySelect } from "../../../components/selects/PrioritySelect";
import { UserContext } from "../../../UserProvider";
import { Authenticated, hasRole } from "../../../models/User";
import { useSelector } from "react-redux";
import { AppState } from "../../../store";
import { useLoadIfEmpty } from "../../../hooks/useLoadIfEmpty";
import { thunkFetchTenantDistributors } from "../../../store/tenants/actions";
import { thunkFetchStores } from "../../../store/stores/actions";

import "react-quill/dist/quill.snow.css";
import { ScanNewProduct } from "../components/manageProduct/ScanNewProduct";
import { ProductMainSection } from "../components/manageProduct/ProductMainSection";
import { SectionCard } from "../components/SectionCard";
import {
  AddCircle,
  Category,
  InsertPhoto,
  LocalShipping,
  Style
} from "@material-ui/icons";
import { PricingSection } from "../components/manageProduct/PricingSection";
import { AutoAwesome, EscalatorWarning } from "@mui/icons-material";
import { Alternative } from "../components/manageProduct/Alternative";
import * as GTIN from "../../../models/GTIN";
import { isOk } from "../../../result";
import { Store } from "../../../models/Store";
import { useI18n } from "../../../hooks/useI18n";

interface State {
  editingProduct: Product;
  image?: ImageInformation;
  adding?: boolean;
  scanning?: boolean;
  loadingProductInformation?: boolean;
  lookupProductFailed?: boolean;
  addingProduct?: boolean;
  saveProductFailed?: boolean;
  gtin?: string;
  error?: string;
  hasAlternatives: boolean;
  alternativeTypes: AlternativeType[];
  alternativeInstances: AlternativeType[];
}

export interface AlternativeType {
  name: string;
  values: string[];
}

export interface TenantProductEditorProps {
  editingProduct: Product;
  onSave?: (product: any) => void;
  image?: ImageInformation;
  adding?: boolean;
}

export const TenantProductEditor: FunctionComponent<
  TenantProductEditorProps
> = ({ editingProduct, onSave, adding, image }) => {
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const currentUser = useContext(UserContext) as Authenticated;
  const { t } = useI18n();

  const tenantDistributors =
    useSelector<AppState, ReadonlyArray<Distributor>>(
      s => s.tenants.distributors
    ) || [];

  const stores = useSelector<AppState, ReadonlyArray<Store>>(
    s => s.stores.stores
  );

  useLoadIfEmpty(!tenantDistributors.length, thunkFetchTenantDistributors);
  useLoadIfEmpty(!stores.length, thunkFetchStores);

  const [addAlternative, setAddAlternative] = useState(false);
  const [showVariantImageDialog, setShowVariantImageDialog] = useState(false);

  const [state, setState] = useState<State>({
    adding: adding,
    scanning: adding,
    loadingProductInformation: false,
    lookupProductFailed: false,
    saveProductFailed: false,
    addingProduct: false,
    editingProduct: {
      ...editingProduct,
      sizing: parseSizing(editingProduct.details.size) ?? ({} as ProductSizing)
    },
    hasAlternatives: false,
    alternativeTypes: [
      { name: t("common.productEditor.colour"), values: [""] },
      { name: t("common.productEditor.size"), values: [""] }
    ] as AlternativeType[],
    alternativeInstances:
      editingProduct.variants && editingProduct.variants.length > 0
        ? editingProduct.variants
            .flatMap(v => v.alternatives)
            .reduce((variantInstances: any, variant: any) => {
              const { key, value } = variant;
              const existing = variantInstances.filter(
                (x: any) => x.key === key
              );
              if (existing.length > 0) {
                variantInstances.map((f: any) => {
                  if (f.key === key) {
                    if (!f.values.includes(value)) {
                      f.values = [...f.values, value];
                    }
                  }
                  return f;
                });
              } else {
                variantInstances = [
                  ...variantInstances,
                  { key: key, values: [value] }
                ];
              }
              return variantInstances;
            }, [])
            .map((v: any) => ({
              name: v.key,
              values: v.values
            }))
        : ([] as AlternativeType[]),
    image: image || { url: "" }
  });
  const [editingVariant, setEditingVariant] = useState<any>();

  useEffect(() => {
    if (state.alternativeInstances.length > 0) createVariants();
  }, [state.alternativeInstances]);

  const disabled = () => {
    const { editingProduct } = state;

    if (editingProduct.name === "") {
      return true;
    }

    if (editingProduct.brandName === "") {
      return true;
    }

    const { pricing } = editingProduct;

    if (pricing.isOpenPricing && pricing.minimumPrice <= 0) {
      return true;
    }

    if (
      !pricing.isOpenPricing &&
      (pricing.price <= 0 || isNaN(pricing.price))
    ) {
      return true;
    }

    if (pricing.vatPercentage == undefined || isNaN(pricing.vatPercentage)) {
      return true;
    }

    if (sizeUnitIsMissingOrIsIncorrect()) {
      return true;
    }

    if (state.editingProduct.variants && state.editingProduct.variants.length) {
      const variantsWithInvalidBarcode = state.editingProduct.variants.filter(
        v => {
          const gtinResult = GTIN.fromString(v.barcode);
          if (!isOk(gtinResult)) {
            return true;
          }
        }
      );

      if (variantsWithInvalidBarcode.length > 0) {
        return true;
      }
    }

    if (
      currentUser.tenant.configuration?.features?.centralOrderingSystem &&
      editingProduct.orderInfo?.orderUnit === OrderUnit.Dfp &&
      !editingProduct.orderInfo.distributionPackageSize
    ) {
      return true;
    }

    return false;
  };

  const sizeUnitIsMissingOrIsIncorrect = () => {
    if (!state?.editingProduct?.sizing?.unit) {
      return true;
    }

    return !["g", "kg", "ml", "cl", "dl", "l", "st"].includes(
      state?.editingProduct?.sizing?.unit
    );
  };

  const addAlternativeInstance = (e: any) => {
    const alternative = state.alternativeTypes.find(
      a => a.name == e.target.value
    );

    if (alternative === undefined) return;

    const deepCopyRule = JSON.parse(JSON.stringify(alternative));

    const instances = [...state.alternativeInstances, deepCopyRule];

    handleAddAlternative(false);

    setState({
      ...state,
      alternativeInstances: instances
    });
  };

  const handleAddAlternative = (add: boolean) => {
    setAddAlternative(add);
  };

  const createVariants = () => {
    const alternatives = state.alternativeInstances.map(x =>
      x.values.map(y => {
        return { key: x.name, value: y };
      })
    );
    const combos = cartesian(...alternatives);

    const variants = combos.map((x: any) => {
      const variantName = x.map((y: any) => y.value).join(", ");
      let currentVariant = state.editingProduct?.variants?.find(
        x => x.name === variantName
      );

      if (currentVariant === undefined) {
        currentVariant = state.editingProduct?.variants?.find(v => {
          const variant = v.alternatives.find(
            (y: any) => y.value == variantName
          );
          if (variant) {
            return v;
          }
        });
      }

      return {
        name: variantName,
        productId: currentVariant?.productId,
        tenantInventoryItemId: currentVariant?.tenantInventoryItemId,
        price: currentVariant?.price ?? 0,
        barcode: currentVariant?.barcode ?? "",
        articleNumber: currentVariant?.articleNumber ?? "",
        image: currentVariant?.image,
        alternatives: x
      };
    });

    setState({
      ...state,
      editingProduct: {
        ...state.editingProduct,
        variants: variants.filter((x: any) => x.name !== "")
      }
      // variants: variants.filter((x: any) => x.name !== "")
    });
  };

  const imageExists = (url: string) => {
    const img = new Image();
    img.src = url;
    if (img?.complete) {
      return true;
    }

    return false;
  };

  const validBarcode = (barcode: string) => {
    const gtinResult = GTIN.fromString(barcode);
    return isOk(gtinResult);
  };

  // @ts-ignore
  return (
    <>
      {state.adding ? (
        <ScanNewProduct
          onScanned={(product, image) => {
            // There is a react kind of a re-render that I don't really understand that makes me do this undefined check
            if (product !== undefined) {
              setState({
                ...state,
                adding: false,
                editingProduct: product,
                image: image
              });
            }
          }}
        />
      ) : (
        <div>
          <Grid container spacing={2} direction={mobile ? "column" : "row"}>
            <Grid item xs={12} lg={8}>
              <ProductMainSection
                product={state.editingProduct}
                onNameChange={name =>
                  setState({
                    ...state,
                    editingProduct: {
                      ...state.editingProduct,
                      name: name
                    }
                  })
                }
                onBrandChange={brand =>
                  setState({
                    ...state,
                    editingProduct: {
                      ...state.editingProduct,
                      brandName: brand
                    }
                  })
                }
                onArticleNumberChange={artNo =>
                  setState({
                    ...state,
                    editingProduct: {
                      ...state.editingProduct,
                      articleNumber: artNo
                    }
                  })
                }
                onDescriptionChange={description =>
                  setState({
                    ...state,
                    editingProduct: {
                      ...state.editingProduct,
                      description: description
                    }
                  })
                }
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <div
                className={classes.fullHeight}
                style={{ margin: 0, padding: 0 }}
              >
                <ImageEditor
                  initial={state.image ?? { url: "" }}
                  onChange={data => setState({ ...state, image: data })}
                  variant="elevation"
                />
              </div>
            </Grid>
          </Grid>
          <Grid container spacing={2} direction={mobile ? "column" : "row"}>
            <Grid item xs={12} lg={8}>
              <PricingSection
                product={state.editingProduct}
                onOpenPricingChange={openPricing =>
                  setState({
                    ...state,
                    editingProduct: {
                      ...state.editingProduct,
                      pricing: {
                        ...state.editingProduct.pricing,
                        isOpenPricing: openPricing
                      }
                    }
                  })
                }
                onPriceUnitChange={priceUnit =>
                  setState({
                    ...state,
                    editingProduct: {
                      ...state.editingProduct,
                      pricing: {
                        ...state.editingProduct.pricing,
                        priceUnit: priceUnit
                      }
                    }
                  })
                }
                onPriceChange={price =>
                  setState({
                    ...state,
                    editingProduct: {
                      ...state.editingProduct,
                      pricing: {
                        ...state.editingProduct.pricing,
                        price: price
                      }
                    }
                  })
                }
                onMinPriceChange={minPrice =>
                  setState({
                    ...state,
                    editingProduct: {
                      ...state.editingProduct,
                      pricing: {
                        ...state.editingProduct.pricing,
                        minimumPrice: minPrice
                      }
                    }
                  })
                }
                onVatChange={(percentage, amount) =>
                  setState({
                    ...state,
                    editingProduct: {
                      ...state.editingProduct,
                      pricing: {
                        ...state.editingProduct.pricing,
                        vatPercentage: percentage,
                        vatAmount: amount
                      }
                    }
                  })
                }
                onAcquisitionPriceChange={acquisitionPrice =>
                  setState({
                    ...state,
                    editingProduct: {
                      ...state.editingProduct,
                      pricing: {
                        ...state.editingProduct.pricing,
                        acquisitionPrice: acquisitionPrice
                      }
                    }
                  })
                }
                onDepositFeeChange={depositFee =>
                  setState({
                    ...state,
                    editingProduct: {
                      ...state.editingProduct,
                      pricing: {
                        ...state.editingProduct.pricing,
                        depositFee: depositFee
                      }
                    }
                  })
                }
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <SectionCard
                icon={
                  <EscalatorWarning
                    style={{ color: theme.palette.warning.dark }}
                  />
                }
                title={t("common.formFields.ageRestriction")}
              >
                <FormControlLabel
                  style={{ marginTop: 10 }}
                  control={
                    <Checkbox
                      checked={state.editingProduct.ageRestricted}
                      onChange={e =>
                        setState({
                          ...state,
                          editingProduct: {
                            ...state.editingProduct,
                            ageRestricted: e.target.checked,
                            manualVerification: false
                          }
                        })
                      }
                    />
                  }
                  label={t("common.formFields.ageRestricted")}
                />
                {state.editingProduct.ageRestricted && (
                  <div style={{ marginBottom: 20 }}>
                    <TextField
                      style={{ flex: 1 }}
                      className={classes.field}
                      label={t("common.formFields.minimumAge")}
                      type={"number"}
                      value={state.editingProduct.minimumAge ?? ""}
                      onChange={e => {
                        setState({
                          ...state,
                          editingProduct: {
                            ...state.editingProduct,
                            minimumAge: e.target.value as any
                          }
                        });
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={state.editingProduct.manualVerification}
                          onChange={e =>
                            setState({
                              ...state,
                              editingProduct: {
                                ...state.editingProduct,
                                manualVerification: e.target.checked
                              }
                            })
                          }
                        />
                      }
                      label={t("common.formFields.manualVerification")}
                    />
                    {state.editingProduct.manualVerification && (
                      <Alert style={{ marginBottom: 10 }} severity={"info"}>
                        Purchase must be manually approved by employee in store
                      </Alert>
                    )}
                  </div>
                )}
              </SectionCard>
            </Grid>
          </Grid>
          <Grid container spacing={2} direction={mobile ? "column" : "row"}>
            <Grid item xs={12} lg={8}>
              <Card style={{ marginBottom: 10 }}>
                <CardContent>
                  <Box display={"flex"} alignItems={"center"}>
                    <LocalShipping
                      style={{ color: theme.palette.primary.dark }}
                    />
                    <Box flexGrow={1} pl={2}>
                      <Typography variant={"h6"} color={"textSecondary"}>
                        {t("common.productEditor.package")}
                      </Typography>
                    </Box>
                  </Box>
                  <Grid container spacing={4} style={{ marginTop: 10 }}>
                    <Grid item xs={12} md={8}>
                      <TextField
                        style={{ width: "100%" }}
                        className={classes.field}
                        label={t("common.formFields.size")}
                        value={state?.editingProduct?.details?.size ?? ""}
                        error={!state?.editingProduct?.sizing?.value}
                        onChange={(e: any) => {
                          const foo = parseSizing(e.target.value);
                          setState({
                            ...state,
                            editingProduct: {
                              ...state.editingProduct,
                              sizing: foo || {},
                              details: {
                                ...state.editingProduct.details,
                                size: e.target.value
                              }
                            }
                          });
                        }}
                      />
                      {state?.editingProduct?.sizing?.value &&
                        sizeUnitIsMissingOrIsIncorrect() && (
                          <Alert severity="error">
                            {t("common.productEditor.sizeUnitMissing")}{" "}
                            <b>g, kg, ml, cl, dl, l, st</b>
                          </Alert>
                        )}
                    </Grid>
                    {currentUser.tenant?.configuration &&
                      currentUser.tenant.configuration?.features
                        ?.centralOrderingSystem && (
                        <>
                          <Grid item xs={12} md={4}>
                            <FormControl
                              style={{ width: "100%" }}
                              variant={"standard"}
                            >
                              <InputLabel id="order-unit-select-label">
                                {t("common.formFields.orderUnit")}
                              </InputLabel>
                              <Select
                                labelId="order-unit-select-label"
                                value={
                                  state.editingProduct.orderInfo?.orderUnit ??
                                  ""
                                }
                                onChange={e => {
                                  setState({
                                    ...state,
                                    editingProduct: {
                                      ...state.editingProduct,
                                      orderInfo: {
                                        ...state.editingProduct.orderInfo,
                                        orderUnit: e.target.value
                                          ? (e.target.value as OrderUnit)
                                          : undefined
                                      }
                                    }
                                  });
                                }}
                              >
                                <MenuItem key={"Kfp"} value={OrderUnit.Kfp}>
                                  Kfp
                                </MenuItem>
                                <MenuItem key={"Dfp"} value={OrderUnit.Dfp}>
                                  Dfp
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            {state.editingProduct.orderInfo?.orderUnit ===
                              OrderUnit.Dfp && (
                              <TextField
                                style={{ width: "100%" }}
                                className={classes.field}
                                label="Quantity *"
                                type="number"
                                value={
                                  state.editingProduct.orderInfo
                                    ?.distributionPackageSize
                                }
                                error={
                                  !state.editingProduct.orderInfo
                                    .distributionPackageSize
                                }
                                onChange={e => {
                                  const value = parseInt(e.target.value);
                                  setState({
                                    ...state,
                                    editingProduct: {
                                      ...state.editingProduct,
                                      orderInfo: {
                                        ...state.editingProduct.orderInfo,
                                        distributionPackageSize: isNaN(value)
                                          ? undefined
                                          : value
                                      }
                                    }
                                  });
                                }}
                              />
                            )}
                          </Grid>
                        </>
                      )}
                  </Grid>
                </CardContent>
              </Card>
              <Card style={{ marginBottom: 10 }}>
                <CardContent>
                  <Box display={"flex"} alignItems={"center"}>
                    <Style style={{ color: theme.palette.primary.dark }} />
                    <Box flexGrow={1} pl={2}>
                      <Typography variant={"h6"} color={"textSecondary"}>
                        {t("common.productEditor.alternatives")}
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    style={{ marginTop: 10 }}
                    control={
                      <Checkbox
                        checked={state.hasAlternatives}
                        onChange={e =>
                          setState({
                            ...state,
                            hasAlternatives: e.target.checked,
                            alternativeInstances: !e.target.checked
                              ? []
                              : state.alternativeInstances
                          })
                        }
                      />
                    }
                    label={t("common.formFields.productHasAlternatives")}
                  />

                  {state.alternativeInstances.map((x, i) => (
                    <>
                      <Alternative
                        alternative={x}
                        key={i}
                        onChange={alternative => {
                          const alternatives = [...state.alternativeInstances];
                          alternatives[i] = alternative;
                          setState({
                            ...state,
                            alternativeInstances: alternatives
                          });
                        }}
                        onDelete={name => {
                          const variants = state.editingProduct.variants?.map(
                            (x: any) => {
                              const aaa = x.alternatives.filter(
                                (a: any) => a.key !== name
                              );

                              x.alternatives = aaa;
                              return x;
                            }
                          );

                          setState({
                            ...state,
                            editingProduct: {
                              ...state.editingProduct,
                              variants:
                                variants &&
                                variants?.flatMap(x => x.alternatives).length >
                                  0
                                  ? variants
                                  : []
                            },
                            alternativeInstances:
                              state.alternativeInstances.filter(
                                x => x.name !== name
                              )
                          });
                        }}
                      />
                      {state.alternativeInstances.length > 1 &&
                        i < state.alternativeInstances.length - 1 && (
                          <Typography
                            color={"primary"}
                            style={{
                              textAlign: "center",
                              fontSize: "0.8rem",
                              fontWeight: "bold"
                            }}
                          >
                            AND
                          </Typography>
                        )}
                    </>
                  ))}

                  {state.alternativeInstances.length > 0 && (
                    <Button
                      onClick={() => handleAddAlternative(true)}
                      style={{ display: !addAlternative ? "flex" : "none" }}
                      color={"primary"}
                      startIcon={
                        <AddCircle style={{ color: "rgb(31, 132, 52)" }} />
                      }
                    >
                      Add another...
                    </Button>
                  )}

                  <FormControl
                    style={{
                      display:
                        state.alternativeInstances.length === 0 ||
                        addAlternative
                          ? "flex"
                          : "none"
                    }}
                    className={classes.field}
                    variant={"standard"}
                    required
                  >
                    <InputLabel id="type-select-label">
                      {t("common.formFields.alternative")}
                    </InputLabel>
                    <Select
                      labelId="type-select-label"
                      value={""}
                      open={addAlternative}
                      onClose={() => handleAddAlternative(false)}
                      onOpen={() => handleAddAlternative(true)}
                      onChange={addAlternativeInstance}
                      disabled={!state.hasAlternatives}
                    >
                      {state.alternativeTypes.map(r => (
                        <MenuItem
                          key={r.name}
                          value={r.name}
                          disabled={state.alternativeInstances.some(
                            a => a.name === r.name
                          )}
                        >
                          {r.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>
              <Card style={{ marginBottom: 10 }}>
                <CardContent>
                  <Box display={"flex"} alignItems={"center"}>
                    <AutoAwesome
                      style={{ color: theme.palette.success.dark }}
                    />
                    <Box flexGrow={1} pl={2}>
                      <Typography variant={"h6"} color={"textSecondary"}>
                        {t("common.productEditor.variants")}
                      </Typography>
                    </Box>
                  </Box>
                  <Grid container spacing={2} style={{ marginTop: 10 }}>
                    {state.editingProduct?.variants?.map((variant: any) => (
                      <div style={{ display: "flex", width: "100%" }}>
                        <TextField
                          style={{ width: "20%" }}
                          className={classes.field}
                          label={t("common.formFields.name")}
                          value={variant.name}
                          InputProps={{
                            readOnly: true,
                            disableUnderline: true,
                            style: { fontWeight: "bold" }
                          }}
                        />
                        <TextField
                          style={{ width: "20%" }}
                          className={classes.field}
                          label={t("common.formFields.price")}
                          type={"number"}
                          value={
                            variant.price === 0
                              ? state.editingProduct.pricing.price
                              : variant.price
                          }
                          onChange={(e: any) => {
                            const variants =
                              state.editingProduct?.variants?.map(x => {
                                if (x.name === variant.name) {
                                  return {
                                    ...x,
                                    price: parseNumberStrict(
                                      e.target.value as string
                                    )
                                  };
                                }
                                return x;
                              });

                            setState({
                              ...state,
                              editingProduct: {
                                ...state.editingProduct,
                                variants: variants
                              }
                            });
                          }}
                        />
                        <TextField
                          style={{ width: "40%", marginLeft: 10 }}
                          className={classes.field}
                          label={t("common.formFields.gtin")}
                          error={!validBarcode(variant.barcode)}
                          helperText={
                            !validBarcode(variant.barcode)
                              ? t("common.productEditor.barcodeMissing")
                              : ""
                          }
                          value={variant.barcode ?? ""}
                          onChange={(e: any) => {
                            const variants =
                              state.editingProduct?.variants?.map(x => {
                                if (x.name === variant.name) {
                                  return {
                                    ...x,
                                    barcode: e.target.value as string
                                  };
                                }
                                return x;
                              });

                            setState({
                              ...state,
                              editingProduct: {
                                ...state.editingProduct,
                                variants: variants
                              }
                            });
                          }}
                        />

                        <TextField
                          style={{ width: "40%", marginLeft: 10 }}
                          className={classes.field}
                          label={t("common.formFields.articleNumber")}
                          value={variant.articleNumber ?? ""}
                          onChange={(e: any) => {
                            const variants =
                              state.editingProduct?.variants?.map(x => {
                                if (x.name === variant.name) {
                                  return {
                                    ...x,
                                    articleNumber: e.target.value as string
                                  };
                                }
                                return x;
                              });

                            setState({
                              ...state,
                              editingProduct: {
                                ...state.editingProduct,
                                variants: variants
                              }
                            });
                          }}
                        />
                        <div style={{ flexGrow: 1 }}>
                          <div style={{ display: "flex" }}>
                            {variant?.image?.url &&
                              variant?.image?.url !== "" &&
                              imageExists(variant.image.url) && (
                                <CardMedia
                                  style={{
                                    maxHeight: 50,
                                    objectFit: "contain"
                                  }}
                                  component={"img"}
                                  image={variant?.image?.url}
                                />
                              )}

                            <IconButton
                              onClick={() => {
                                setShowVariantImageDialog(true);
                                setEditingVariant(variant);
                              }}
                              style={{
                                color: theme.palette.grey[500]
                              }}
                            >
                              <InsertPhoto />
                            </IconButton>
                          </div>
                        </div>
                      </div>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} lg={4}>
              <SectionCard
                icon={<Category color={"primary"} />}
                title={t("common.actions.organizeProduct")}
              >
                <Box sx={{ marginTop: 20 }}>
                  <CategorySelect
                    label={t("common.formFields.category")}
                    className={classes.field}
                    value={
                      state?.editingProduct?.category.id !== ""
                        ? state?.editingProduct?.category.id
                        : undefined
                    }
                    onSelected={category =>
                      setState({
                        ...state,
                        editingProduct: {
                          ...state.editingProduct,
                          category: category
                        }
                      })
                    }
                  />

                  {!hasRole(currentUser, ["DistributorManager"]) && (
                    <DistributorSelect
                      label={t("common.formFields.distributor")}
                      className={classes.field}
                      value={
                        state?.editingProduct.orderInfo?.distributor?.id ?? ""
                      }
                      onSelectedStrict={distributor => {
                        setState({
                          ...state,
                          editingProduct: {
                            ...state.editingProduct,
                            orderInfo: {
                              ...state.editingProduct.orderInfo,
                              distributor: distributor
                            }
                          }
                        });
                      }}
                    />
                  )}
                  <PrioritySelect
                    className={classes.field}
                    value={state.editingProduct.priority?.id ?? ""}
                    onSelected={priority =>
                      setState({
                        ...state,
                        editingProduct: {
                          ...state.editingProduct,
                          priority: priority
                        }
                      })
                    }
                  />
                </Box>
              </SectionCard>
            </Grid>
          </Grid>
        </div>
      )}
      {!state.adding && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: 20,
            marginBottom: 20
          }}
        >
          <Button
            style={{ width: 200 }}
            variant="contained"
            color="primary"
            disabled={disabled() || state.addingProduct}
            onClick={async () => {
              setState({ ...state, addingProduct: true });
              let result;

              let product = state.editingProduct;
              if (currentUser?.distributorId) {
                let distributor = tenantDistributors.find(
                  x => x.id === currentUser.distributorId
                );

                product.orderInfo = {
                  ...product.orderInfo,
                  distributor: distributor
                };
              }

              if (product.id === "00000000-0000-0000-0000-000000000000") {
                result = await ProductApi.register(product);
              } else {
                result = await ProductApi.update(
                  product.tenantInventoryItemId,
                  stores[0].id,
                  product
                );
              }

              if (result.type === "OK") {
                if (state.image?.blob) {
                  if (product.id !== "00000000-0000-0000-0000-000000000000") {
                    await ProductApi.uploadImage(product.id, state.image.blob);
                  } else {
                    await ProductApi.uploadImage(
                      result.value,
                      state.image.blob
                    );
                  }
                }
                if (onSave) {
                  onSave(product);
                }
              } else {
                setState({
                  ...state,
                  saveProductFailed: true
                });
              }
            }}
          >
            {t("common.actions.save")}{" "}
            {state.addingProduct && (
              <CircularProgress size={20} style={{ marginLeft: 20 }} />
            )}
          </Button>

          {state?.saveProductFailed == true && (
            <div
              style={{
                display: "flex",
                marginTop: 20,
                justifyContent: "center"
              }}
            >
              <Alert severity="error">{state.error}</Alert>
            </div>
          )}
        </div>
      )}

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"md"}
        onClose={() => setShowVariantImageDialog(false)}
        open={showVariantImageDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle
          disableTypography={true}
          style={{
            paddingBottom: 4,
            paddingTop: 8,
            borderBottom: `1px solid ${theme.palette.grey.A100}`,
            marginBottom: 10
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <Typography variant={"h6"}>
              Add image to the: <b>{editingVariant?.name}</b> variant
            </Typography>
            <IconButton
              aria-label="close"
              style={{ color: theme.palette.grey[500] }}
              onClick={() => setShowVariantImageDialog(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <ImageEditor
            initial={editingVariant?.image ?? { url: "" }}
            onChange={data => {
              setEditingVariant({ ...editingVariant, image: data });
            }}
            variant="elevation"
          />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 20,
              marginBottom: 20
            }}
          >
            <Button
              style={{ width: 200 }}
              variant="contained"
              color="primary"
              onClick={async () => {
                const variants = state.editingProduct.variants?.map(v => {
                  if (v.name === editingVariant?.name) {
                    v = editingVariant;
                  }

                  return v;
                });
                setState({
                  ...state,
                  editingProduct: {
                    ...state.editingProduct,
                    variants: variants
                  }
                });
                setShowVariantImageDialog(false);
                setEditingVariant(undefined);
              }}
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
